import { PackageMinus, Undo2 } from 'lucide-react';
import { useState } from 'react';
import type { Mouvement, Product } from '@sirajoptic/shared';
import { BadgeMouvement } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Combobox, type OptionCombobox } from '@/components/ui/combobox';
import { InputField } from '@/components/ui/field';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { useProduitsPourSelection } from '@/features/stock/api/stock.api';
import { useAntiRebond } from '@/lib/anti-rebond';
import { dateHeure, dh, entier } from '@/lib/format';
import { useAnnulerDeduction, useDeduireStock } from '../api/commandes.api';

/**
 * Déduction de stock rattachée à la commande — règle 11.
 *
 * Le rattachement d'une monture se fait maintenant dans le formulaire lui-même
 * (voir `MontureInput`), et l'exemplaire sort tout seul à la livraison. Cet
 * écran reste indispensable pour tout le reste : un verre, une lentille, un
 * produit d'entretien, une seconde paire — et pour les dix mille commandes
 * importées du cahier, dont les colonnes sont du texte libre sans fiche
 * produit. C'est aussi la seule voie pour sortir plusieurs exemplaires.
 */
export function DeductionStock({
  commandeId,
  mouvements,
  desactive,
}: {
  commandeId: string;
  mouvements: Mouvement[];
  desactive?: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [produit, setProduit] = useState<Product | null>(null);
  const [quantite, setQuantite] = useState('1');
  const [erreur, setErreur] = useState<string | null>(null);

  /* Sans anti-rebond, chaque lettre balayait le stock. */
  const rechercheDifferee = useAntiRebond(recherche);
  const { data, isFetching } = useProduitsPourSelection(rechercheDifferee);
  const deduire = useDeduireStock();
  const annuler = useAnnulerDeduction();
  const toast = useToast();

  const options: OptionCombobox<Product>[] = (data?.data ?? []).map((p) => ({
    cle: p.id,
    libelle: p.nom,
    description: `${entier(p.quantite)} en stock · ${dh(p.prix_vente)}`,
    valeur: p,
  }));

  async function valider() {
    if (!produit) {
      setErreur('Choisissez un produit du stock.');
      return;
    }
    const n = Number(quantite);
    if (!Number.isInteger(n) || n < 1) {
      setErreur('Quantité invalide.');
      return;
    }
    try {
      await deduire.mutateAsync({
        id: commandeId,
        input: { product_id: produit.id, quantite: n, note: null },
      });
      toast.succes(`${n} × ${produit.nom} déduit du stock.`);
      setProduit(null);
      setRecherche('');
      setQuantite('1');
      setErreur(null);
      setOuvert(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Déduction impossible.');
    }
  }

  async function compenser(mouvement: Mouvement) {
    try {
      await annuler.mutateAsync({ id: commandeId, mouvementId: mouvement.id });
      toast.succes('Déduction annulée, le stock est restitué.');
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Annulation impossible.');
    }
  }

  // Le bouton « annuler » n'a de sens que si le produit est encore sorti :
  // on regarde le solde net de la commande, annulations comprises.
  const soldeParProduit = new Map<string, number>();
  for (const m of mouvements) {
    soldeParProduit.set(m.product_id, (soldeParProduit.get(m.product_id) ?? 0) + m.delta);
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">Stock rattaché</h3>
        {!desactive ? (
          <Button
            size="sm"
            variant="outline"
            icone={<PackageMinus aria-hidden className="h-3.5 w-3.5" />}
            onClick={() => setOuvert((o) => !o)}
          >
            Déduire du stock
          </Button>
        ) : null}
      </div>

      {ouvert ? (
        <div className="mb-3 grid grid-cols-1 items-end gap-2 rounded-md border border-mustard-300 bg-mustard-50 p-3 sm:grid-cols-[2fr_1fr_auto]">
          <Combobox<Product>
            label="Produit"
            valeur={produit?.nom ?? recherche}
            chargement={isFetching || recherche !== rechercheDifferee}
            options={options}
            placeholder="Rechercher dans le stock…"
            videMessage="Aucun produit trouvé"
            onChangeTexte={(t) => {
              setRecherche(t);
              setProduit(null);
            }}
            onSelectionner={(o) => {
              setProduit(o.valeur);
              setRecherche(o.valeur.nom);
            }}
            erreur={erreur && !produit ? erreur : undefined}
          />
          <InputField
            label="Quantité"
            type="number"
            min="1"
            step="1"
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
            erreur={erreur && produit ? erreur : undefined}
          />
          <Button variant="primary" onClick={() => void valider()} chargement={deduire.isPending}>
            Déduire
          </Button>
        </div>
      ) : null}

      {mouvements.length === 0 ? (
        <Encart>
          Aucun article du stock n'est rattaché à cette commande. La monture et les verres saisis
          ci-dessus sont du texte libre : ils ne décrémentent pas le stock automatiquement.
        </Encart>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {mouvements.map((m) => {
            const encoreDeduit = (soldeParProduit.get(m.product_id) ?? 0) < 0;
            return (
              <li key={m.id} className="flex items-center gap-3 px-3 py-2">
                <BadgeMouvement raison={m.raison} />
                <span className="min-w-0 flex-1 truncate text-sm text-ink">
                  {m.produit_nom ?? 'Produit supprimé'}
                </span>
                <span
                  className={`shrink-0 text-sm font-semibold tabular ${
                    m.delta > 0 ? 'text-success' : 'text-danger'
                  }`}
                >
                  {m.delta > 0 ? '+' : ''}
                  {entier(m.delta)}
                </span>
                <span className="hidden w-32 shrink-0 text-right text-xs text-ink-muted sm:block">
                  {dateHeure(m.created_at)}
                </span>
                {m.raison === 'VENTE' && encoreDeduit && !desactive ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Annuler cette déduction"
                    title="Annuler cette déduction"
                    onClick={() => void compenser(m)}
                    disabled={annuler.isPending}
                  >
                    <Undo2 aria-hidden className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
